import { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Raven from 'raven-js';
import { useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import PortalIdParser from 'PortalIdParser';
import { getAuthorizationDataStatus, getApplicationAuthorizationData, getInternallyDevelopedAppIdsStatus, getIsInternallyDeveloped, getIsRequestingNewScopesStatus, getAuthorizeApplicationErrorSubCategory, getAuthorizeApplicationErrorContext } from '../../selectors/AppSelectors';
import AuthorizeHeader from '../AuthorizeHeader';
import LoadingStateWrapper from '../LoadingStateWrapper';
import AuthorizeBody from '../AuthorizeBody';
import { fetchApplicationAuthorizationData, fetchIsRequestingNewScopes } from '../../actions/AppActions';
import { getAuthorizationRequestInfo } from '../../utils/urls';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIAlert from 'UIComponents/alert/UIAlert';
import { getShouldAllowDevPortalInstall, isValidClientId } from '../../utils/authUtils';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { getHasOnlyDevelopersParcel } from '../../selectors/ScopeSelectors';
import { fetchEcosystemDetails } from '../../actions/EcosystemActions';
import { getEcosystemDetailsStatus } from '../../selectors/EcosystemSelectors';
import { LISTING_STATUS } from '../../data/models/ApplicationAuthorization';
import { getAccountsFetchStatus } from '../../selectors/AccountSelectors';
import { fetchAccountById } from '../../actions/AccountActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AuthorizeContainer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const authorizationDataStatus = useSelector(getAuthorizationDataStatus);
  const authorizationData = useSelector(getApplicationAuthorizationData);
  const internallyDevelopedStatus = useSelector(getInternallyDevelopedAppIdsStatus);
  const isInternallyDeveloped = useSelector(getIsInternallyDeveloped);
  const hasOnlyDevelopersParcel = useSelector(getHasOnlyDevelopersParcel);
  const isRequestingNewScopesStatus = useSelector(getIsRequestingNewScopesStatus);
  const accountsFetchStatus = useSelector(getAccountsFetchStatus);
  const authorizeApplicationErrorSubCategory = useSelector(getAuthorizeApplicationErrorSubCategory);
  const authorizeApplicationErrorContext = useSelector(getAuthorizeApplicationErrorContext);
  const shouldAllowDevPortalInstall = useMemo(() => authorizationDataStatus.succeeded && getShouldAllowDevPortalInstall(authorizationData), [authorizationData, authorizationDataStatus]);
  useEffect(() => {
    if (accountsFetchStatus.uninitialized) {
      dispatch(fetchAccountById()).catch(e => Raven.captureException(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsFetchStatus]);
  const authorizationRequestInfo = useMemo(() => getAuthorizationRequestInfo(location.search), [location.search]);
  const clientId = authorizationRequestInfo.clientId;
  const clientIdIsValid = isValidClientId(clientId);
  useEffect(() => {
    if (clientIdIsValid) {
      dispatch(fetchApplicationAuthorizationData({
        data: authorizationRequestInfo
      })).catch(e => Raven.captureException(e));
      dispatch(fetchIsRequestingNewScopes(authorizationRequestInfo)).catch(e => Raven.captureException(e));
    }
  }, [dispatch, authorizationRequestInfo, clientIdIsValid]);
  const ecosystemDetailsStatus = useSelector(getEcosystemDetailsStatus);
  const isOnMarketplace = authorizationDataStatus.succeeded && authorizationData && authorizationData.getListingStatus() !== LISTING_STATUS.UNLISTED;
  useEffect(() => {
    if (isOnMarketplace) {
      dispatch(fetchEcosystemDetails(authorizationData.appId)).catch(e => Raven.captureException(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnMarketplace]);
  const renderAlert = ({
    rhumbName,
    content
  }) => {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: rhumbName,
      children: /*#__PURE__*/_jsx(UIAlert, {
        className: "m-y-4",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.authorization.errorSubHeader"
        }),
        type: "danger",
        children: content
      })
    });
  };
  const renderMessageForErrorCode = () => {
    const potentialI18nKey = `oauth.${authorizeApplicationErrorSubCategory}`;
    return isValidI18nKey(potentialI18nKey) ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: potentialI18nKey,
      options: authorizeApplicationErrorContext
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "oauth.AuthorizationErrorType.UNEXPECTED_ERROR"
    });
  };
  const renderAuthContent = () => {
    if (authorizationDataStatus.pending || authorizationDataStatus.uninitialized) {
      return null;
    }
    if (authorizationDataStatus.warning) {
      // There was a non-server related error with the App Auth (user isn't authorized, wrong client_id, etc)
      return renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD',
        content: renderMessageForErrorCode()
      });
    } else if (hasOnlyDevelopersParcel && !shouldAllowDevPortalInstall) {
      // We're blocking an install into a developer portal
      return renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD',
        content: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          options: {
            portalId: `${PortalIdParser.get()}`
          },
          message: `oauth.errors.DEVELOPER_PORTAL`
        })
      });
    } else {
      // We successfully authed
      return /*#__PURE__*/_jsxs(NavMarker, {
        name: "AUTH_PAGE_LOAD",
        children: [/*#__PURE__*/_jsx(AuthorizeHeader
        // App logo
        , {
          providerIconUrl: authorizationData.listingIconUrl,
          providerName: authorizationData.name
        }), /*#__PURE__*/_jsx(AuthorizeBody, {
          authorizationData: authorizationData,
          authorizationDataStatus: authorizationDataStatus,
          isInternallyDeveloped: isInternallyDeveloped
        })]
      });
    }
  };
  if (!clientIdIsValid) {
    return /*#__PURE__*/_jsx(UIFlex, {
      align: "center",
      justify: "center",
      direction: "column",
      children: renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD',
        content: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "oauth.errors.BAD_CLIENT_ID"
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(UIFlex, {
    align: "center",
    justify: "center",
    direction: "column",
    children: /*#__PURE__*/_jsx(LoadingStateWrapper, {
      status: authorizationDataStatus.succeeded ? [internallyDevelopedStatus, accountsFetchStatus, isRequestingNewScopesStatus, ...(isOnMarketplace ? [ecosystemDetailsStatus] : [])] : authorizationDataStatus,
      showLoadingIfUnfetched: true,
      error: renderAlert({
        rhumbName: 'AUTH_PAGE_LOAD_ERROR',
        content: renderMessageForErrorCode()
      }),
      children: renderAuthContent()
    })
  });
};
export default AuthorizeContainer;